import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderHistoryItem } from "../../interfaces/history";
import { ITableColumn } from "../../interfaces/Table/TableColumn";
import { Align } from "../shared/Table/style";
import OrderHistoryDetailsModal from "./details/OrderHistoryDetailsModal";
import HistoryTableShell from "./HistoryTableShell";
import {
  buildActionsColumn,
  buildMarketColumn,
  buildOrderTypeColumn,
  buildPriceColumn,
  buildSideColumn,
  buildSizeFilledColumn,
  buildTimestampColumn,
} from "./columns";
import { PositionEffectTag } from "./style";
import {
  DecoratedFields,
  useDecoratedHistoryItems,
} from "./useDecoratedHistoryItems";

interface IOrderHistoryTableProps {
  items: OrderHistoryItem[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  height?: number;
}

type DecoratedOrder = OrderHistoryItem & DecoratedFields;

function OrderHistoryTable({
  items,
  isLoading,
  isLoadingMore,
  hasMore,
  onLoadMore,
  height,
}: IOrderHistoryTableProps) {
  const { t } = useTranslation("app", {
    keyPrefix: "components.History.OrderHistoryTable",
  });
  const { t: translateAll } = useTranslation();
  const { t: tooltips } = useTranslation("tooltips");
  const decorated = useDecoratedHistoryItems<OrderHistoryItem>(items);
  const [details, setDetails] = useState<DecoratedOrder>();

  const columns: ITableColumn<DecoratedOrder>[] = useMemo(() => {
    const ctx = { t, tooltips, translateAll };
    return [
      buildMarketColumn<DecoratedOrder>(ctx),
      buildSideColumn<DecoratedOrder>(ctx),
      buildOrderTypeColumn<DecoratedOrder>(ctx),
      buildSizeFilledColumn<DecoratedOrder>(ctx, { priceAccessor: "price" }),
      buildPriceColumn<DecoratedOrder>(ctx, { accessor: "price" }),
      {
        title: t("status", { defaultValue: "Status" }),
        accessor: "order_status",
        tooltip: tooltips("orderStatus"),
        Cell: ({ value }) => (
          <Align align="left">
            {t(String(value).toLowerCase(), { defaultValue: String(value) })}
          </Align>
        ),
      },
      {
        title: t("flags", { defaultValue: "Flags" }),
        accessor: "reduce_only",
        tooltip: tooltips("orderFlags", {
          defaultValue:
            "PO = Post Only (maker-only, cancels if it would take). RO = Reduce Only (can only reduce, never grow or flip a position).",
        }),
        Cell: ({ row }) => {
          const flags: string[] = [];
          if (row.original.post_only) flags.push("PO");
          if (row.original.reduce_only) flags.push("RO");
          if (!flags.length) return "-";
          return (
            <Align align="left">
              {flags.map((f) => (
                <PositionEffectTag key={f}>{f}</PositionEffectTag>
              ))}
            </Align>
          );
        },
      },
      buildTimestampColumn<DecoratedOrder>(ctx, {
        accessor: "created_timestamp",
        unit: "seconds",
      }),
      buildActionsColumn<DecoratedOrder>("order_id", (row) => setDetails(row)),
    ];
  }, [t, tooltips, translateAll]);

  return (
    <>
      <OrderHistoryDetailsModal
        order={details}
        onHide={() => setDetails(undefined)}
      />
      <HistoryTableShell<DecoratedOrder>
        columns={columns}
        items={decorated}
        isLoading={isLoading}
        isLoadingMore={isLoadingMore}
        hasMore={hasMore}
        onLoadMore={onLoadMore}
        onRowClick={(row) => setDetails(row)}
        emptyText={t("no_order_history", {
          defaultValue: "No order history yet.",
        })}
        height={height}
      />
    </>
  );
}

export default OrderHistoryTable;
